(function furavoraCartShippingModule(global) {
  'use strict';

  const VERSION = '2.67.0';
  const THRESHOLD_CENTS = 10000;
  const STANDARD_SHIPPING_CENTS = 1000;

  /**
   * Quote the store's shipping presentation from the already-discounted
   * merchandise subtotal. An empty cart never receives a shipping charge.
   * The actual checkout rate is configured and enforced in Shopify Admin.
   */
  function quoteForSubtotal(subtotalCents, itemCount) {
    const subtotal = Math.max(0, Math.round(Number(subtotalCents) || 0));
    const items = Math.max(0, Math.round(Number(itemCount) || 0));
    const hasItems = items > 0;
    const isFree = hasItems && subtotal >= THRESHOLD_CENTS;
    const shippingCents = hasItems && !isFree ? STANDARD_SHIPPING_CENTS : 0;

    return {
      subtotalCents: subtotal,
      itemCount: items,
      hasItems,
      isFree,
      free: isFree,
      shippingCents,
      remainingCents: hasItems ? Math.max(0, THRESHOLD_CENTS - subtotal) : THRESHOLD_CENTS,
      estimatedTotalCents: subtotal + shippingCents,
      progressPercent: hasItems ? Math.min(100, Math.max(0, (subtotal / THRESHOLD_CENTS) * 100)) : 0,
    };
  }

  function formatUsd(cents) {
    const value = Math.max(0, Math.round(Number(cents) || 0)) / 100;
    return `US$${value.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  const baseApi = Object.freeze({
    VERSION,
    THRESHOLD_CENTS,
    STANDARD_SHIPPING_CENTS,
    quoteForSubtotal,
    formatUsd,
  });

  if (typeof module !== 'undefined' && module.exports) module.exports = baseApi;
  if (!global || !global.document) return;

  const document = global.document;
  if (document.documentElement.dataset.fvCartShippingV267Ready === 'true') {
    global.FuravoraCartShipping?.refresh?.();
    return;
  }
  document.documentElement.dataset.fvCartShippingV267Ready = 'true';

  const raf = global.requestAnimationFrame?.bind(global) || ((callback) => global.setTimeout(callback, 0));
  const cancelRaf = global.cancelAnimationFrame?.bind(global) || global.clearTimeout?.bind(global);
  const reduceMotion = global.matchMedia?.('(prefers-reduced-motion: reduce)').matches === true;
  let renderFrame = 0;

  /**
   * Read only the cart represented by this summary. This prevents the full
   * cart and drawer copies of the same line from being counted twice.
   */
  function selectedCartState(summary, fullSubtotalOverride) {
    const lines = new Map();
    const drawer = summary.closest('.cart-drawer');
    const lineRoot = drawer || document.querySelector('cart-items') || document;

    lineRoot.querySelectorAll('[data-fv-cart-line]').forEach((line) => {
      const key = line.dataset.fvLineKey;
      if (!key || lines.has(key)) return;

      const serverLineCents = Math.max(0, Math.round(Number(
        line.dataset.fvShippingLineTotalCents || line.dataset.fvLineTotalCents || 0
      ) || 0));
      const serverQuantity = Math.max(1, Math.round(Number(line.dataset.fvShippingLineQuantity || 1) || 1));
      const quantityInput = line.querySelector('.quantity__input');
      const currentQuantity = Math.max(0, Math.round(Number(quantityInput?.value ?? serverQuantity) || 0));
      const quantityChanged = currentQuantity !== serverQuantity;
      const optimisticLineCents = quantityChanged
        ? Math.max(0, Math.round((serverLineCents / serverQuantity) * currentQuantity))
        : serverLineCents;
      const selectionControl = line.querySelector('[data-fv-cart-select]');

      lines.set(key, {
        cents: optimisticLineCents,
        quantity: currentQuantity,
        selected: selectionControl ? selectionControl.checked : true,
        quantityChanged,
      });
    });

    const selectedLines = [...lines.values()].filter((line) => line.selected);
    const allLinesSelected = lines.size > 0 && selectedLines.length === lines.size;
    const hasOptimisticQuantity = selectedLines.some((line) => line.quantityChanged);
    const fallbackSubtotal = Math.max(0, Number(summary.dataset.fullSubtotalCents || 0));
    const authoritativeFullSubtotal = Number.isFinite(fullSubtotalOverride)
      ? Math.max(0, Number(fullSubtotalOverride))
      : fallbackSubtotal;
    const fallbackItemCount = Math.max(0, Number(summary.dataset.itemCount || 0));
    const computedSubtotal = selectedLines.reduce((sum, line) => sum + line.cents, 0);
    const computedItemCount = selectedLines.reduce((sum, line) => sum + line.quantity, 0);

    return {
      subtotalCents: lines.size
        ? (allLinesSelected && !hasOptimisticQuantity ? authoritativeFullSubtotal : computedSubtotal)
        : authoritativeFullSubtotal,
      itemCount: lines.size ? computedItemCount : fallbackItemCount,
    };
  }

  function animateSummary(summary, bar, targetPercent) {
    const targetWidth = `${targetPercent}%`;
    const hasRendered = summary.dataset.fvShippingRendered === 'true';

    if (bar) {
      if (!reduceMotion && !hasRendered) {
        bar.style.width = '0%';
        // Force the zero-width frame to paint before filling the meter.
        void bar.offsetWidth;
        raf(() => {
          if (bar.isConnected) bar.style.width = targetWidth;
        });
      } else {
        bar.style.width = targetWidth;
      }
    }

    if (!reduceMotion) {
      summary.classList.remove('is-updating');
      void summary.offsetWidth;
      summary.classList.add('is-updating');
      global.setTimeout(() => summary.classList.remove('is-updating'), 520);
    }
    summary.dataset.fvShippingRendered = 'true';
  }

  function renderSummary(summary, mainFullSubtotal) {
    const fullSubtotalOverride = summary.closest('.cart-drawer') ? undefined : mainFullSubtotal;
    const state = selectedCartState(summary, fullSubtotalOverride);
    const quote = quoteForSubtotal(state.subtotalCents, state.itemCount);
    const compact = summary.classList.contains('fv-shipping-progress--dock');
    const copy = summary.querySelector('[data-fv-shipping-progress-copy]');
    const note = summary.querySelector('[data-fv-shipping-progress-note]');
    const current = summary.querySelector('[data-fv-shipping-current]');
    const merchandise = summary.querySelector('[data-fv-merchandise-subtotal]');
    const bar = summary.querySelector('[data-fv-shipping-progress-bar]');
    const track = summary.querySelector('[role="progressbar"]');
    const feeLabel = summary.querySelector('[data-fv-shipping-fee-label]');
    const fee = summary.querySelector('[data-fv-shipping-fee]');
    const estimatedTotal = summary.querySelector('[data-fv-estimated-total]');

    if (copy) {
      copy.textContent = !quote.hasItems
        ? 'ADD AN ITEM TO START YOUR SHIPPING PROGRESS'
        : quote.isFree
          ? 'FREE SHIPPING UNLOCKED'
          : `ADD ${formatUsd(quote.remainingCents)} MORE FOR FREE SHIPPING`;
    }
    if (note) {
      note.textContent = !quote.hasItems
        ? 'Free standard shipping begins at US$100.00.'
        : quote.isFree
          ? 'US$10.00 standard shipping has been removed from this estimate.'
          : 'US$10.00 standard shipping is included in the estimate below.';
    }
    if (current) current.textContent = quote.hasItems ? formatUsd(quote.subtotalCents) : 'US$0.00';
    if (merchandise) merchandise.textContent = quote.hasItems ? formatUsd(quote.subtotalCents) : '—';
    if (track) {
      track.setAttribute('aria-valuenow', String(Math.round(quote.progressPercent)));
      track.setAttribute(
        'aria-valuetext',
        quote.isFree ? 'Free shipping unlocked' : `${formatUsd(quote.remainingCents)} remaining`
      );
    }
    if (feeLabel) feeLabel.textContent = compact || quote.isFree ? 'Shipping ·' : 'Estimated standard shipping ·';
    if (fee) fee.textContent = !quote.hasItems ? '—' : quote.isFree ? 'FREE' : 'US$10.00';
    if (estimatedTotal) estimatedTotal.textContent = quote.hasItems ? formatUsd(quote.estimatedTotalCents) : '—';

    summary.classList.toggle('is-unlocked', quote.isFree);
    summary.classList.toggle('is-empty', !quote.hasItems);
    summary.style.setProperty('--fv-shipping-progress', `${quote.progressPercent}%`);
    summary.dataset.currentSubtotalCents = String(quote.subtotalCents);
    summary.dataset.currentShippingCents = String(quote.shippingCents);
    summary.dataset.currentEstimatedTotalCents = String(quote.estimatedTotalCents);
    animateSummary(summary, bar, quote.progressPercent);
  }

  function renderAll() {
    if (renderFrame) cancelRaf?.(renderFrame);
    renderFrame = raf(() => {
      renderFrame = 0;
      const mainSummary = document.querySelector('#main-cart-footer .js-contents [data-fv-shipping-summary]');
      const mainFullSubtotal = mainSummary
        ? Math.max(0, Number(mainSummary.dataset.fullSubtotalCents || 0))
        : undefined;
      document.querySelectorAll('[data-fv-shipping-summary]').forEach((summary) => {
        renderSummary(summary, mainFullSubtotal);
      });
    });
  }

  function renderAfterCartAction() {
    renderAll();
    // Dawn replaces cart sections asynchronously. These fallbacks cover custom
    // cart apps that update existing nodes rather than inserting new markup.
    global.setTimeout(renderAll, 80);
    global.setTimeout(renderAll, 450);
  }

  global.FuravoraCartShipping = Object.freeze({ ...baseApi, refresh: renderAll });

  document.addEventListener('input', (event) => {
    if (event.target.matches('.quantity__input')) renderAll();
  });
  document.addEventListener('change', (event) => {
    if (event.target.matches('[data-fv-cart-select], [data-fv-cart-select-all], .quantity__input')) {
      renderAfterCartAction();
    }
  });
  document.addEventListener('click', (event) => {
    if (event.target.closest('.quantity__button, cart-remove-button')) renderAfterCartAction();
  });
  document.addEventListener('cart:refresh', renderAfterCartAction);
  document.addEventListener('shopify:section:load', renderAfterCartAction);
  global.addEventListener?.('pageshow', renderAll);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', renderAll, { once: true });
  }

  const observer = new MutationObserver((mutations) => {
    const cartChanged = mutations.some((mutation) => {
      if (mutation.type === 'attributes') return true;
      return [...mutation.addedNodes].some((node) => (
        node.nodeType === 1 && (
          node.matches?.('[data-fv-cart-line], [data-fv-shipping-summary]') ||
          node.querySelector?.('[data-fv-cart-line], [data-fv-shipping-summary]')
        )
      ));
    });
    if (cartChanged) renderAfterCartAction();
  });
  observer.observe(document.body, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['data-fv-shipping-line-total-cents', 'data-fv-shipping-line-quantity'],
  });

  renderAll();
})(typeof window !== 'undefined' ? window : globalThis);
